import React, { useContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Badge from 'react-bootstrap/Badge';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_SUCCESS':
            return { ...state, inventory: action.payload, loading: false };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.payload };
        case 'CREATE_REQUEST':
            return { ...state, loadingCreate: true };
        case 'CREATE_SUCCESS':
            return { ...state, loadingCreate: false, successCreate: true };
        case 'CREATE_FAIL':
            return { ...state, loadingCreate: false };
        case 'CREATE_RESET':
            return { ...state, successCreate: false };
        case 'DELETE_REQUEST':
            return { ...state, loadingDelete: true, successDelete: false };
        case 'DELETE_SUCCESS':
            return { ...state, loadingDelete: false, successDelete: true };
        case 'DELETE_FAIL':
            return { ...state, loadingDelete: false };
        case 'DELETE_RESET':
            return { ...state, successDelete: false };
        default:
            return state;
    }
};

const CATEGORIES = {
    Stock: ['Vegetables', 'Flour', 'Drinks', 'Fruits', 'Meat', 'Dairy', 'Spices', 'Other'],
    Equipment: ['Utensils', 'Furniture', 'Kitchen Appliances', 'Cleaning', 'Other'],
};

export default function InventoryScreen() {
    const [{ loading, error, inventory, loadingCreate, successCreate, successDelete }, dispatch] = useReducer(reducer, {
        loading: true,
        error: '',
        inventory: [],
    });

    const { state } = useContext(Store);
    const { userInfo } = state;

    const [activeTab, setActiveTab] = useState('Stock');
    const [showModal, setShowModal] = useState(false);

    // Form fields
    const [name, setName] = useState('');
    const [quantity, setQuantity] = useState(0);
    const [unit, setUnit] = useState('');
    const [costPrice, setCostPrice] = useState(0);
    const [lowStockThreshold, setLowStockThreshold] = useState(10);
    const [itemType, setItemType] = useState('Stock');
    const [category, setCategory] = useState('Other');
    const [status, setStatus] = useState('Active');
    const [location, setLocation] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({ type: 'FETCH_REQUEST' });
                const { data } = await axios.get(`/api/inventory?itemType=${activeTab}`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                dispatch({ type: 'FETCH_SUCCESS', payload: data });
            } catch (err) {
                dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
            }
        };
        if (successCreate || successDelete) {
            dispatch({ type: successCreate ? 'CREATE_RESET' : 'DELETE_RESET' });
            fetchData();
        } else {
            fetchData();
        }
    }, [userInfo, successCreate, successDelete, activeTab]);

    const submitHandler = async (e) => {
        e.preventDefault();
        try {
            dispatch({ type: 'CREATE_REQUEST' });
            const itemData = {
                name, quantity, unit, costPrice, lowStockThreshold,
                itemType, category, status, location
            };
            if (isEdit) {
                await axios.put(
                    `/api/inventory/${editId}`,
                    itemData,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Item updated successfully');
            } else {
                await axios.post(
                    '/api/inventory',
                    itemData,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Item added successfully');
            }
            dispatch({ type: 'CREATE_SUCCESS' });
            setShowModal(false);
            resetForm();
        } catch (err) {
            toast.error(getError(err));
            dispatch({ type: 'CREATE_FAIL' });
        }
    };

    const deleteHandler = async (item) => {
        if (window.confirm('Are you sure to delete?')) {
            try {
                dispatch({ type: 'DELETE_REQUEST' });
                await axios.delete(`/api/inventory/${item._id}`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                toast.success('Item deleted successfully');
                dispatch({ type: 'DELETE_SUCCESS' });
            } catch (err) {
                toast.error(getError(err));
                dispatch({ type: 'DELETE_FAIL' });
            }
        }
    };

    const resetForm = () => {
        setName('');
        setQuantity(0);
        setUnit(activeTab === 'Stock' ? '' : 'pcs'); // Default to pieces for equipment
        setCostPrice(0);
        setLowStockThreshold(10);
        setItemType(activeTab);
        setCategory('Other');
        setStatus('Active');
        setLocation('');
        setIsEdit(false);
        setEditId('');
    };

    const editHandler = (item) => {
        setName(item.name);
        setQuantity(item.quantity);
        setUnit(item.unit);
        setCostPrice(item.costPrice);
        setLowStockThreshold(item.lowStockThreshold);
        setItemType(item.itemType || 'Stock');
        setCategory(item.category || 'Other');
        setStatus(item.status || 'Active');
        setLocation(item.location || '');
        setIsEdit(true);
        setEditId(item._id);
        setShowModal(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return <Badge bg="success">Active</Badge>;
            case 'In Use': return <Badge bg="primary">In Use</Badge>;
            case 'Maintenance': return <Badge bg="warning" text="dark">Maintenance</Badge>;
            case 'Destroyed': return <Badge bg="danger">Destroyed</Badge>;
            default: return <Badge bg="secondary">{status}</Badge>;
        }
    };

    return (
        <Container fluid className="px-5">
            <Helmet>
                <title>Inventory & Equipment Management</title>
            </Helmet>
            <Row className="my-4 align-items-center">
                <Col>
                    <h1 className="fw-bold">Professional Inventory</h1>
                    <p className="text-muted">Manage your stock and equipment assets with precision.</p>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" className="shadow-sm" onClick={() => { resetForm(); setShowModal(true); }}>
                        <i className="fas fa-plus me-2"></i> Add New Item
                    </Button>
                </Col>
            </Row>

            <Tabs
                id="inventory-tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4 custom-tabs"
            >
                <Tab eventKey="Stock" title={<span><i className="fas fa-boxes me-2"></i>General Stock</span>}>
                    {renderInventoryTable()}
                </Tab>
                <Tab eventKey="Equipment" title={<span><i className="fas fa-tools me-2"></i>Utensils & Equipment</span>}>
                    {renderInventoryTable()}
                </Tab>
            </Tabs>

            {renderModal()}
        </Container>
    );

    function renderInventoryTable() {
        if (loading) return <LoadingBox />;
        if (error) return <MessageBox variant="danger">{error}</MessageBox>;

        return (
            <div className="bg-white p-4 rounded-3 shadow-sm">
                <Table responsive hover className="align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Item Name</th>
                            <th>Category</th>
                            {activeTab === 'Stock' ? <th>Quantity</th> : <th>Status</th>}
                            {activeTab === 'Stock' && <th>Unit</th>}
                            <th>Cost (RWF)</th>
                            <th>Location</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inventory.map((item) => (
                            <tr key={item._id}>
                                <td className="fw-bold">{item.name}</td>
                                <td>
                                    <Badge pill bg="light" text="dark" className="border">
                                        {item.category}
                                    </Badge>
                                </td>
                                <td>
                                    {activeTab === 'Stock' ? (
                                        <span className={item.quantity <= item.lowStockThreshold ? 'text-danger fw-bold' : ''}>
                                            {item.quantity} {item.quantity <= item.lowStockThreshold && <i className="fas fa-exclamation-triangle ms-1" title="Low Stock"></i>}
                                        </span>
                                    ) : (
                                        getStatusBadge(item.status)
                                    )}
                                </td>
                                {activeTab === 'Stock' && <td>{item.unit}</td>}
                                <td>{item.costPrice.toLocaleString()}</td>
                                <td>{item.location || <span className="text-muted small">Not set</span>}</td>
                                <td className="text-center">
                                    <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={() => editHandler(item)}>
                                        <i className="fas fa-edit me-1"></i> Edit
                                    </Button>
                                    <Button variant="outline-danger" size="sm" className="ms-2 rounded-pill px-3" onClick={() => deleteHandler(item)}>
                                        <i className="fas fa-trash me-1"></i> Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {inventory.length === 0 && (
                            <tr>
                                <td colSpan="7" className="text-center py-5 text-muted">
                                    No {activeTab.toLowerCase()} items found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    }

    function renderModal() {
        return (
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">{isEdit ? 'Update Asset' : 'Add New Asset'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={submitHandler}>
                    <Modal.Body className="p-4">
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Item Name</Form.Label>
                                    <Form.Control value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Tomatoes, Spoons, Oven" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Item Type</Form.Label>
                                    <Form.Select value={itemType} onChange={(e) => {
                                        setItemType(e.target.value);
                                        setCategory('Other');
                                    }}>
                                        <option value="Stock">General Stock (Perishables/Supplies)</option>
                                        <option value="Equipment">Equipment (Assets/Utensils)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Category</Form.Label>
                                    <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                                        {CATEGORIES[itemType].map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Location / Storage</Form.Label>
                                    <Form.Control value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Kitchen, Store Room" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />

                        {itemType === 'Stock' ? (
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Quantity</Form.Label>
                                        <Form.Control type="number" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} required />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Unit</Form.Label>
                                        <Form.Control value={unit} onChange={(e) => setUnit(e.target.value)} required placeholder="kg, pieces, etc." />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Low Stock Alarm</Form.Label>
                                        <Form.Control type="number" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(Number(e.target.value))} required />
                                    </Form.Group>
                                </Col>
                            </Row>
                        ) : (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Asset Status</Form.Label>
                                        <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                                            <option value="Active">Active / Ready</option>
                                            <option value="In Use">In Use</option>
                                            <option value="Maintenance">Maintenance / Repair</option>
                                            <option value="Destroyed">Destroyed / Disposed</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Quantity (Units)</Form.Label>
                                        <Form.Control type="number" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} required />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Cost Price per unit (RWF)</Form.Label>
                                    <Form.Control type="number" value={costPrice} onChange={(e) => setCostPrice(Number(e.target.value))} required />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" className="px-4" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button variant="primary" className="px-4" type="submit" disabled={loadingCreate}>
                            {loadingCreate ? 'Processing...' : (isEdit ? 'Update Asset' : 'Add to Inventory')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }
}
